import { useAuth } from '../context/AuthContext';

const ROLE_RANK = {
  viewer:         1,
  operator:       2,
  admin:          3,
  // NL21-A10.1 — the Company Representative sits ABOVE admin on the server's
  // ladder (`_PROJECT_ROLE_RANK`): every door an admin passes, it passes. Its
  // rank here keeps that order (admin < CR < platform owner); without an entry
  // it read as rank 0 — "no access" — on every screen.
  company_representative: 3.5,
  platform_owner: 4,
  // legacy fallbacks
  project_admin:  3,
  inspector:      2,
  subcontractor:  2,
};

/**
 * LC9 — the external project role, and why it is not in ROLE_RANK above.
 *
 * `client_representative` sits OUTSIDE the `viewer < operator < admin` ladder
 * on the server (docs/design/asset-lifecycle.md §5.1), and the frontend mirrors
 * that rather than approximating it. Giving it a rank here would make it
 * ORDERED against the others, and then every `rank >= X` comparison in this
 * file would answer a question that has no answer — the client may accept a
 * delivery, which no operator may, and may not create an asset, which every
 * operator may.
 *
 * So it is handled by NAME, ahead of the rank lookup, and every write flag is
 * false. The three things it CAN write (raise a punch item, verify one, accept
 * the asset) are not general capabilities and are not expressible as
 * `canCreate` — they are three specific acts, and the screens that offer them
 * read `isClientRep` and offer exactly those.
 */
export const CLIENT_REPRESENTATIVE_ROLE = 'client_representative';

/**
 * Synchronous project-scoped permission lookup. NO network round-trip.
 * Reads from the in-memory cache populated by AuthContext on mount.
 *
 * Platform owner: tenant-wide shortcut, all-access regardless of projectId.
 * Other users: lookup in the cached Map<projectId, role>.
 *
 * When projectId is null/undefined, returns no-access (caller should pass
 * the resolved projectId once available; isLoading reflects whether the
 * AuthContext bulk fetch has completed).
 *
 * Every return shape carries `isClientRep` so that a caller destructuring it
 * never gets `undefined` from one branch and `false` from another.
 */
export function useProjectPermissions(projectId) {
  const { user, projectAccessesMap, accessesLoading } = useAuth();

  // Platform owner: tenant-wide shortcut
  if (user?.role === 'platform_owner') {
    return {
      role: 'platform_owner',
      isLoading: false,
      canView: true,
      canCreate: true,
      canEdit: true,
      isAdmin: true,
      isPlatformOwner: true,
      // Never. Seniority does not make somebody the customer — the server
      // refuses a platform owner the acceptance for exactly this reason
      // (§5.7), and a UI that offered the button would be promising something
      // the backend will refuse.
      isClientRep: false,
    };
  }

  // Company QA Admin: tenant-wide shortcut within their company.
  // Backend enforces the tenant boundary (user_has_project_access checks
  // project.company_id == user.company_id); this hook only renders UI.
  if (user?.role === 'company_qa_admin') {
    return {
      role: 'company_qa_admin',
      isLoading: false,
      canView: true,
      canCreate: true,
      canEdit: true,
      isAdmin: true,
      isPlatformOwner: false,
      isClientRep: false,
    };
  }

  const role = projectId ? (projectAccessesMap.get(projectId) || null) : null;

  // LC9 — by NAME, before the rank lookup, because this role has no rank.
  // Reading it through ROLE_RANK would give it 0 and therefore the same shape
  // as "no access at all", which is wrong in the one direction that matters:
  // this user CAN see the project, and the screens need to know it is them.
  if (role === CLIENT_REPRESENTATIVE_ROLE) {
    return {
      role,
      isLoading: accessesLoading,
      // They read the project like an operator reads it (§5.3.1) — every read
      // surface is granted to them by name on the server.
      canView: true,
      // Every write flag false, including canCreate. The three acts they do
      // have are offered by `isClientRep`, individually, on the screens that
      // own them. A truthy canCreate here would light up dozens of "+ Add"
      // buttons across the app whose endpoints all refuse them.
      canCreate: false,
      canEdit: false,
      isAdmin: false,
      isPlatformOwner: false,
      isClientRep: true,
    };
  }

  const rank = role ? (ROLE_RANK[role] || 0) : 0;

  return {
    role,
    isLoading: accessesLoading,
    canView: rank >= ROLE_RANK.inspector,
    canCreate: rank >= ROLE_RANK.inspector,
    canEdit: rank >= ROLE_RANK.project_admin,
    isAdmin: rank >= ROLE_RANK.project_admin,
    isPlatformOwner: false,
    isClientRep: false,
  };
}
